import { Col, Row } from '@advanza/grid'
import { Button, Divider, Icon, PreIcon } from '@advanza/ui'
import { addPaymentData } from 'actions/providers'
import Card from 'components/Card'
import CustomPricesList from 'components/customPrices/CustomPricesList'
import EventFilter from 'components/events/EventFilter'
import EventList from 'components/events/EventList'
import LogsList from 'components/logs/LogsList'
import HubooOrderStatusButton from 'components/misc/HubooOrderStatusButton'
import NotesFilter from 'components/notes/NotesFilter'
import NotesList from 'components/notes/NotesList'
import AvailabilityCalendarDatesList from 'components/pauses/AvailabilityCalendarDatesList'
import PauseDatesList from 'components/pauses/PauseDatesList'
import SnoozeDatesList from 'components/pauses/SnoozeDatesList'
import Brochures from 'components/providers/Brochures'
import ChatbotDpaWidget from 'components/providers/chatbotDpa/ChatbotDpaWidget'
import ChatbotKnowledgeWidget from 'components/providers/chatbotKnowledge/ChatbotKnowledgeWidget'
import CustomerSatisfaction, {
    useCustomerSatisfaction,
} from 'components/providers/CustomerSatisfaction'
import FranchiseSelect from 'components/providers/FranchiseSelect'
import FranchiseSettings from 'components/providers/FranchiseSettings'
import FreeLeads from 'components/providers/FreeLeads'
import HealthScoreWidget from 'components/providers/healthScore/HealthScoreWidget'
import LeadBoostersOffice from 'components/providers/LeadBoostersOffice'
import LeadSettings from 'components/providers/LeadSettings'
import LinkExchangeWidget from 'components/providers/LinkExchangeWidget'
import MarketingPromos from 'components/providers/MarketingPromos'
import PaymentInfoEntity from 'components/providers/PaymentInfoEntity'
import PresenceSubscriptionSettings from 'components/providers/PresenceSubscriptionSettings'
import ProviderBusinessInfoEntity from 'components/providers/ProviderBusinessInfoEntity'
import ProviderCreditLimitSettings from 'components/providers/ProviderCreditLimitSettings'
import ProviderHubspot from 'components/providers/ProviderHubspot'
import ProviderLabelPicker from 'components/providers/ProviderLabelPicker'
import ProviderNativeAppCard from 'components/providers/ProviderNativeAppCard'
import ProviderNativeAppStats from 'components/providers/ProviderNativeAppStats'
import ProvidersPlatformsList from 'components/providers/ProvidersPlatformsList'
import ProviderStatus from 'components/providers/ProviderStatus'
import QualityScores from 'components/providers/QualityScores'
import RemoveFromBounceListButton from 'components/providers/RemoveFromBounceListButton'
import RoiCalculator from 'components/providers/RoiCalculator'
import SalesPromos from 'components/providers/SalesPromos'
import ScheduledTerminationCard from 'components/providers/ScheduledTerminationCard'
import SubscriptionList from 'components/subscriptions/SubscriptionList'
import SubscriptionsFilter from 'components/subscriptions/SubscriptionsFilter'
import HappinessIcon from 'components/ui/HappinessIcon'
import AiUpsellAdvise from 'components/UpsellSalesAdviceChatBot/AiUpsellAdvise'
import UserView from 'components/users/UserView'
import auroraStyle from 'css/aurora.module.css'
import { useAppDispatch } from 'hooks/hooks'
import { useProvider } from 'hooks/providerHooks'
import { Helmet } from 'react-helmet-async'

const ProviderViewOverview = ({ providerId }) => {
    const { provider, user = {}, entities } = useProvider(providerId)
    const { recalculateHappiness } = useCustomerSatisfaction(providerId)
    const dispatch = useAppDispatch()
    const filterIdAssociations = `p_${providerId}`

    return (
        <div>
            <Helmet>
                <title>Office - {provider.business_name}</title>
            </Helmet>
            <Row>
                <Col sm={6}>
                    <Card>
                        <ProviderStatus
                            providerId={providerId}
                            glowing={provider.hasActiveLeadBooster}
                        />
                    </Card>
                    <Divider m />
                    <Card icon="settings" header="Lead settings">
                        <Divider />
                        <LeadSettings
                            entityId={provider.lead_setting}
                            providerId={providerId}
                            provider={provider}
                            logs={entities.logs}
                        />
                    </Card>
                    {(provider.isSigned || provider.scheduledTermination) && (
                        <>
                            <Divider m />
                            <ScheduledTerminationCard providerId={Number(providerId)} />
                        </>
                    )}
                    <Divider m />
                    <Card icon="Loyalty" header="Presence Subscription">
                        <PresenceSubscriptionSettings provider={provider} />
                    </Card>
                    <Divider m />
                    <AiUpsellAdvise providerId={providerId} />
                    <Divider m />
                    <Card icon="campaign" header="Marketing Promos">
                        <Divider />
                        <MarketingPromos
                            key={`promos-${provider.isSigned}-${provider.status}`}
                            providerId={Number(providerId)}
                            providerName={provider?.business_name}
                        />
                    </Card>
                    <Divider m />
                    <Card icon="money_off" header="Free leads & Free trial">
                        <Divider />
                        <FreeLeads providerId={providerId} />
                    </Card>
                    {!provider.isSignedOrFreeTrial && (
                        <>
                            <Divider m />
                            <Card
                                icon="😴"
                                iconStyle={{
                                    overflow: 'visible',
                                    maxWidth: 'none',
                                    fontFamily: 'sans-serif',
                                }}
                                header="Snooze">
                                <SnoozeDatesList provider={provider} />
                            </Card>
                        </>
                    )}
                    <Divider m />
                    <ProviderNativeAppCard
                        providerId={providerId}
                        mobileAppInstalled={Boolean(provider.mobile_app_installed)}
                    />
                    {provider.availabilityCalendarEnabled && (
                        <>
                            <Divider m />
                            <Card icon="calendar_month" header="Availability calendar">
                                <AvailabilityCalendarDatesList provider={provider} />
                            </Card>
                        </>
                    )}
                    <Divider m />
                    <Card icon="pause_circle" header="Pause dates">
                        <PauseDatesList provider={provider} />
                    </Card>
                    <Divider m />
                    {provider.isSigned && (
                        <>
                            <Card
                                className={provider.hasActiveLeadBooster && auroraStyle.aurora}
                                icon="insights"
                                header="Lead booster"
                                infoHover={
                                    <div className="flex flex-col gap-2">
                                        <div>
                                            <b>Lead Booster</b>: Increases leads received (50% extra
                                            charge, respects weekly limits).
                                        </div>
                                        <div>
                                            <b>Lead Booster Promo Leads</b>: Additional leads
                                            without extra charge.
                                        </div>
                                    </div>
                                }>
                                <Divider />
                                <LeadBoostersOffice providerId={Number(providerId)} />
                            </Card>
                            <Divider m />
                        </>
                    )}
                    <Card icon="local_offer" header="Sales Promo">
                        <Divider />
                        <SalesPromos providerId={providerId} />
                    </Card>
                    <Divider m />
                    <Card icon="preview" header="Link exchange">
                        <Divider />
                        <LinkExchangeWidget providerId={providerId} />
                    </Card>
                    {provider.isSignedOrFreeTrial && (
                        <>
                            <Divider m />
                            <Card icon="menu_book" header="Brochure">
                                <Divider />
                                <Brochures providerId={providerId} />
                            </Card>
                        </>
                    )}
                    <Divider m />
                    <Card header="Business info" icon="business">
                        <ProviderBusinessInfoEntity entityId={providerId} />
                    </Card>
                    <Divider m />
                    {provider.use_active_campaign && <ProviderHubspot providerId={providerId} />}
                    <Divider m />
                </Col>
                <Col sm={6}>
                    <Card icon="filter_list" header="Filters">
                        <Divider s />
                        {provider.notes && (
                            <SubscriptionsFilter
                                defaultOptions={{ providerIds: [providerId] }}
                                compact
                                hidePagination
                                providerId={providerId}
                                filterId={filterIdAssociations}
                            />
                        )}
                        <Divider />
                        <SubscriptionList compact hideHeader filterId={filterIdAssociations} />
                    </Card>
                    <Divider m />
                    <Card header="Office labels" icon="label">
                        <ProviderLabelPicker entityId={providerId} wrapValues />
                    </Card>
                    <Divider m />
                    <Card
                        header={
                            <>
                                Notes
                                {provider.freshsalesSalesOwner && (
                                    <span style={{ fontSize: 'smaller' }}>
                                        {' - '} Accountmanager: {provider.freshsalesSalesOwner}
                                    </span>
                                )}
                            </>
                        }
                        icon="speaker_notes">
                        {provider.notes && (
                            <NotesFilter
                                filterId={filterIdAssociations}
                                defaultOptions={{ providerIds: [providerId], pageSize: 10 }}
                                newEntityOptions={{
                                    type: 'serviceProviders',
                                    foreign_key: providerId,
                                }}
                            />
                        )}
                        <NotesList filterId={filterIdAssociations} />
                    </Card>
                    {provider.isSigned && (
                        <>
                            <Divider m />
                            <HealthScoreWidget providerId={providerId} />
                            <Divider m />
                            <ChatbotKnowledgeWidget providerId={providerId} />
                            <Divider m />
                            <ChatbotDpaWidget providerId={providerId} />
                            <Divider m />
                            <Card
                                header={
                                    <Row middle="xs">
                                        <Col x>Customer satisfaction</Col>
                                        <Col x style={{ lineHeight: 0 }}>
                                            <Button
                                                name="text"
                                                disabled={provider._isLoadingSatisfaction}
                                                onClick={() => recalculateHappiness()}>
                                                <PreIcon icon="refresh">Recalculate</PreIcon>
                                            </Button>
                                        </Col>
                                    </Row>
                                }
                                topRight={
                                    <>
                                        Current mood:{' '}
                                        <HappinessIcon
                                            happiness={provider.last_satisfaction_score?.happiness}
                                        />
                                    </>
                                }
                                icon="sentiment_satisfied">
                                <Divider m />
                                <CustomerSatisfaction providerId={providerId} />
                            </Card>
                        </>
                    )}
                    <Divider m />
                    <Card header="Quality score" icon="thumbs_up_down">
                        <QualityScores providerId={providerId} />
                    </Card>
                    <Divider m />
                    <Card header="Payment" icon="euro_symbol">
                        <Divider />
                        {provider.payment_info && (
                            <PaymentInfoEntity entityId={provider.payment_info} />
                        )}
                        {!provider.payment_info && (
                            <button
                                className="plain"
                                onClick={() => dispatch(addPaymentData(providerId))}>
                                <Icon>add</Icon> Add payment data
                            </button>
                        )}
                        {provider.payment_info && (
                            <>
                                <HubooOrderStatusButton providerId={providerId} />
                            </>
                        )}
                    </Card>
                    <Divider m />
                    <Card header="Credit Limits" icon="credit_card">
                        <Divider />
                        <ProviderCreditLimitSettings providerId={providerId} />
                    </Card>
                    <Divider m />
                    <Card header="ROI" icon="trending_up">
                        <Divider />
                        <RoiCalculator providerId={Number(providerId)} />
                    </Card>
                    <Divider m />
                    <Card header="Custom Prices" icon="euro_symbol">
                        <CustomPricesList providerId={providerId} />
                    </Card>
                    <Divider m />
                    <Card header={`User #${user.id}`} icon="person">
                        <UserView userId={user.id} useStore="providers" />
                    </Card>
                    <Divider m />
                    <Card header="Franchise" icon="layers">
                        <Divider m />
                        {provider.franchise_status ? (
                            <FranchiseSettings providerId={providerId} />
                        ) : (
                            <FranchiseSelect providerId={providerId} />
                        )}
                    </Card>
                    <Divider m />
                    <Card header="Active on" icon="sports_mma">
                        <Divider m />
                        <ProvidersPlatformsList providerId={providerId} />
                    </Card>
                    <Divider m />
                </Col>
            </Row>
            <ProviderNativeAppStats providerId={providerId} />
            <Divider m />
            <Card header="Events" icon="event_note" useToggle>
                <EventFilter
                    filterId={filterIdAssociations}
                    defaultOptions={{
                        providerIds: [providerId],
                        pageSize: 10,
                    }}
                />
                <EventList compact filterId={filterIdAssociations} />
                <Divider m />
                <RemoveFromBounceListButton userId={provider.user} />
            </Card>
            <Divider m />
            <Card header="Logs" icon="text_snippet" useToggle>
                <LogsList
                    filterId={`pro_${providerId}_logs`}
                    options={{
                        providerIds: [providerId],
                        excludeLogTypes: ['whatsapp_usage_send', 'whatsapp_usage_received'],
                        pageSize: 50,
                    }}
                />
            </Card>
            <Divider m />
            <Card header="Whatsapp logs" icon="whatsapp_green" s3 useToggle>
                <LogsList
                    filterId={`pro_${providerId}_whatsapp_logs`}
                    options={{
                        providerIds: [providerId],
                        logTypes: ['whatsapp_usage_send', 'whatsapp_usage_received'],
                        pageSize: 10,
                    }}
                    selectableLogTypes={[
                        { value: 'whatsapp_usage_send', name: 'Send' },
                        { value: 'whatsapp_usage_received', name: 'Received' },
                    ]}
                    allowEmptySelection={false}
                />
            </Card>
            <div style={{ height: '50vh' }} />
            <Divider xl />
        </div>
    )
}

export default ProviderViewOverview
